import { Icon } from "@iconify/react";
import React, { useEffect, useRef, useState } from "react";
import { FreeMode, Navigation, Keyboard, A11y } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { useLanguage } from "../../hooks/use-language";
import { useTranslationsById } from "../../hooks/use-translations-by-id";
import { useId } from "../../hooks/use-id";

export const CommunityMonthSelector = ({ months, onSelect, defaultMonthId }) => {
	const [currentMonth, setCurrentMonth] = useState(null);
	const swiperRef = useRef();
	const visibleMonths = Array.isArray(months)
		? [...months].filter((month) => (month?.visible === true && (month?.empty === false || month?.empty === undefined)))
		: [];
	const nextButtonRef = useRef();
	const prevButtonRef = useRef();

	const lang = useLanguage();
	const txts = useTranslationsById({ mainSectionName: "CommunityMonthSelector", language: lang })
	const carrouselId = useId('carousel-month-status');

	const handleOnSelect = (month) => {
		if (month?.id !== currentMonth?.id) {
			setCurrentMonth(month);
			onSelect && onSelect(month);
			const monthIndex = visibleMonths.findIndex((m) => m.id === month?.id);
			focusMonth(monthIndex);
		}
	};

	const handleDefaultSelectedMonth = () => {
		const firstElementVisible = visibleMonths?.find((month) => month.visible === true);
		const firstElementVisibleIndex = visibleMonths?.findIndex(
			(month) => month.visible === true
		);
		if (firstElementVisible) {
			setCurrentMonth(firstElementVisible);
			focusMonth(firstElementVisibleIndex);
		}
	};

	const focusMonth = (monthIndex) => {
		if (swiperRef.current && swiperRef.current.slideTo) {
			swiperRef.current.slideTo(monthIndex);
		}
	};

	const updateNavigation = (swiper) => {
		swiper.params.navigation.prevEl = prevButtonRef.current;
		swiper.params.navigation.nextEl = nextButtonRef.current;
		swiper.navigation.destroy();
		swiper.navigation.init();
		swiper.navigation.update();
	};

	useEffect(() => {
		if (swiperRef.current) {
			updateNavigation(swiperRef.current);
		}
		const ids = visibleMonths.map((month) => month.id.toLowerCase());
		if (defaultMonthId && ids.includes(defaultMonthId.toLowerCase())) {
			const month = visibleMonths.find((m) => m.id === defaultMonthId && m.visible === true);
			const monthIndex = visibleMonths.findIndex(
				(m) => m.id === defaultMonthId && m.visible === true
			);
			if (month) {
				setCurrentMonth(month);
				focusMonth(monthIndex);
			} else {
				handleDefaultSelectedMonth();
			}
		} else {
			handleDefaultSelectedMonth();
		}
	}, [defaultMonthId, JSON.stringify(visibleMonths), swiperRef.current]);

	return (
		<div
			className="w-100 community-month-selector d-flex justify-content-center align-items-center my-4"
			role="region"
			aria-label={txts?.getText({id:"selector", defaultText:"Selector de mes"})}
		>
			<button className="nav-button col-auto d-none d-sm-flex focusable-red" ref={prevButtonRef} aria-label={txts?.getText({id:"previous_month", defaultText:"Mes anterior"})}>
				<Icon icon="fa-solid:angle-left" width={20} />
			</button>
			<Swiper
				className="col overflow-hidden"
				slidesPerView={"auto"}
				slidesPerGroupAuto
				freeMode={{
					enabled: true,
				}}
				onSwiper={(s) => {
					swiperRef.current = s;
					updateNavigation(s);
				}}
				on
				navigation={{
					nextEl: nextButtonRef.current,
					prevEl: prevButtonRef.current,
					enabled: true,
				}}
				a11y={{
					containerMessage: txts.getText({ id: "monsths_year", defaultText: "Meses del año" }),
					itemRoleDescriptionMessage: txts.getText({ id: "mounth", defaultText: "mes" }),
					scrollOnFocus: true
				}}
				onSlideChange={(swiper) => {
					const activeSlide = swiper.slides[swiper.activeIndex];
					if (activeSlide) {
						activeSlide.focus();
					}
					const status = document.getElementById(carrouselId);
					if (status) {
						status.textContent = `${txts.getText({ id: "mounth", defaultText: "mes" })}  ${swiper.realIndex + 1} ${txts.getText({ id: "of", defaultText: "de" })} ${visibleMonths.length}`;
					}
				}}
				keyboard={true}
				modules={[Navigation, FreeMode, Keyboard, A11y]}
			>
				<div className="d-flex py-5">
					{visibleMonths.map((month, i) => {
						return (
							<>
								{month?.visible ? (
									<SwiperSlide key={month?.id} className="w-auto">
										<button
											key={month?.id}
											className={`p-2 m-3 item focusable-red ${currentMonth?.id === month?.id ? "active" : ""
												}`}
											onClick={() => handleOnSelect(month)}
											aria-current={currentMonth?.id === month?.id  ? true : false}
										>
											{month?.name} {`${month?.year ? month?.year : ''}`}
										</button>
									</SwiperSlide>
								) : null}
							</>
						);
					})}
				</div>
			</Swiper>
			<button className="col-auto nav-button d-none d-sm-flex focusable-red" ref={nextButtonRef} aria-label={ txts.getText({ id: "next_month", defaultText: "Mes siguiente"})}>
				<Icon icon="fa-solid:angle-right" width={20} />
			</button>
			<div aria-live="polite" className="sr-only" id={carrouselId}></div>
		</div>
	);
};
